import React, { FC,useRef,useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Container,
  TopInfoWrapper,
  HeadWrapper,
  RightHeadContainer,
  InstanceName,
  NavWrapper,
  BodyWrapper,
} from './styles'
import {
  InfoBlock,
  BackArrow,
  Button,
  TopProgress,
  ModalVariables,
  InfoSkeleton,
  DropOptions,
  Table,
  Select
} from '../../../../components'
import { useNavigate, useParams } from 'react-router-dom'
import MenuButton from '../../../../components/button/menu-button'
import { icons } from '../../../../icons'
import { PropsContainer } from '../../../../components/table/styles'
import { flavorService } from '../../../../api'
import { useUi } from '../../../../context/ui.context'
import {formatFileSize} from '../../../../utils/sizes'
import {timeConverterMMDDYYHHMM} from '../../../../utils/dates'
import { handleOsIcons } from '../../../../hooks/useOsIcons'
import { useLocalStorage } from '../../../../hooks/useLocalStorage'
import Skeleton from 'react-loading-skeleton'
import config from '../../../../config'
import { InstanceDot, InstanceHeadInfo, InstanceStatus } from '../../Instances/components/instance-page/styles'
import { handleStatusName } from '../../../../utils/handle-status-name'
import { NumberParam, StringParam, useQueryParam } from 'use-query-params'
import { StateWrapper, GreenDot, Greentext } from '../../Instances/styles'
import { usePageTitle } from '../../../../context/navigationContext'

const FlavorGroupPage:FC = () => {
  const [t] = useTranslation()
  const navigate = useNavigate()
  const {groupId} = useParams()
  const {viewAlert} = useUi()
  const [options, setOptions] = useState<boolean>(false)
  const [flavorGroup, setFlavorGroup] = useState<any>()
  const [flavors, setFlavors] = useState<any[]>([])
  const [allFlavors, setAllFlavors] = useState<any[]>([])
  const [page, setPage] = useQueryParam('page', NumberParam);
  const [ordering, setOrdering] = useQueryParam('ordering', StringParam);
  const [pageSize, setPageSize] = useQueryParam('page_size', NumberParam);
  const [paginationData, setPaginationData] = useState<any>()
  const [pageLoading, setPageLoading] = useState<boolean>(true)
  const [viewModal, setViewModal] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [activeAnimation, setActiveAnimation] = useState<boolean>(false)
  const [modalType, setModalType] = useState<any>('')
  const [alertTitle, setAlertTitle] = useState<any>('')
  const [alertSubTitle, setAlertSubTitle] = useState<any>()
  const [selectedFlavor, setSelectedFlavor] = useState<any>(null)
  const [selectedFlavorToRemove, setSelectedFlavorToRemove] = useState<any>(null)
  const {setDataToLs} = useLocalStorage()
  usePageTitle(flavorGroup ? `${t("PAGE_TITLE_FLAVOR_GROUP_DETAIL")}: ${flavorGroup?.name || flavorGroup?.id}` : t("PAGE_TITLE_FLAVOR_GROUP_DETAIL"))
  const dropsRef:any = useRef()
  const optionsRef:any = useRef()

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  },[]);
  
  const handleClickOutside = (event:any) => {
    if (optionsRef && !optionsRef.current.contains(event.target) && !dropsRef.current.contains(event.target)){
      setOptions(false)
    }
  }
  
  const _updateFlavorGroup = () => {
    if(groupId) {
      flavorService.getFlavorGroup(groupId)
      .then((res) => {
        setFlavorGroup(res.data)
        setPageLoading(false)
      })
      .catch((err) => {
        if(err?.response?.data?.detail){
          viewAlert({
            severity: 'error',
            message: err.response?.data?.detail
          })
        }
        navigate('/openstack/flavor-groups')
        setPageLoading(false)
      })
    }
  }

  const _updateFlavorsInGroup = () => {
    if(groupId) {
      flavorService.getFlavorsInGroup(groupId, {
        page: page || 1,
        pageSize: pageSize || 10,
        ordering: ordering,
      })
      .then((res) => {
        setFlavors(res.data.objects.map((flavor:any) => {
          return {
            ...flavor,
            navigate: `/openstack/flavors/${flavor.id}`,
            vcpus: flavor.vcpus,
            memory_mb: `${(flavor.memory_mb / 1024).toFixed(1)} GB (${flavor.memory_mb} MB)`,
            disk_gb: `${flavor.root_gb} GB`,
            ephemeral_gb: `${flavor.ephemeral_gb} GB`,
            swap: `${flavor.swap} MB`,
            name: flavor.name || flavor.display_name || flavor.id,
            is_public: flavor.is_public ? `${t("YES")}` : `${t("NO")}`,
            out_of_stock: flavor.out_of_stock ? `${t("YES")}` : `${t("NO")}`,
            status:(
              <StateWrapper>
                <GreenDot active={!flavor.out_of_stock ? "available" : "out_of_stock"}/>
                <Greentext active={!flavor.out_of_stock ? "available" : "out_of_stock"}>
                  {handleStatusName(flavor.out_of_stock ? "out_of_stock" : "available", t)}
                </Greentext>
              </StateWrapper>),
          }
        }))
        setPaginationData(res.data)
      })
      .catch((err) => {
        console.error('Error fetching flavors in group:', err)
      })
    }
  }

  const _loadAllFlavors = () => {
    flavorService.getFlavors({
      page: 1,
      pageSize: 1000,
      ordering: '',
    })
    .then((res) => {
      setAllFlavors(res.data.objects.map((flavor:any) => ({
        ...flavor,
        label: flavor.display_name || flavor.name || flavor.id,
        value: flavor.id,
      })))
    })
    .catch((err) => {
      console.error('Error loading all flavors:', err)
    })
  }
  
  useEffect(() => {
    _updateFlavorGroup()
    _updateFlavorsInGroup()
    _loadAllFlavors()
    const interval = setInterval(() => {
      _updateFlavorGroup()
      _updateFlavorsInGroup()
    },5000)
    return () => {
      clearInterval(interval)
    }
  },[page, ordering, pageSize])
  
  const specifications = [
    {
      title:t("ID"),
      id:'1',
      value:flavorGroup?.id,
      copy:true,
    },
    {
      title:t("NAME"),
      id:'2',
      value:flavorGroup?.name
    },
    {
      title:t("DESCRIPTION"),
      id:'3',
      value:flavorGroup?.description || t("NO_DESCRIPTION")
    },
    {
      title:t("FLAVOR_COUNT"),
      id:'4',
      value:flavorGroup?.flavor_count || 0
    },
    {
      title:t("PRIORITY"),
      id:'5',
      value:flavorGroup?.priority
    },
    {
      title:t("IS_DEFAULT"),
      id:'6',
      value:flavorGroup?.is_default ? t("YES") : t("NO")
    },
    {
      title:t("SELECTED_BY_DEFAULT"),
      id:'7',
      value:flavorGroup?.selected_by_default ? t("YES") : t("NO")
    },
    {
      title:t("CREATED_AT"),
      id:'8',
      value:timeConverterMMDDYYHHMM(flavorGroup?.created_at)
    },
  ]

  const flavorItems = [
    {
      title:t("REMOVE_FROM_GROUP"),
      id:'1',
      onClick:(flavor:any) => {
        openModal('removeFromGroup')
        setAlertTitle(t("FLAVOR"))
        setAlertSubTitle([flavor])
        setSelectedFlavorToRemove(flavor)
      },
      icon:icons.DeleteIcon,
      divider:false
    },
  ]

  const flavorColumns = [
    {
      title:t("NAME"),
      key:'name',
    },
    {
      title:t("STATUS"),
      key:'status'
    },
    {
      title:t("VCPUS"),
      key:'vcpus'
    },
    {
      title:t("MEMORY"),
      key:'memory_mb'
    },
    {
      title:t("DISK"),
      key:'disk_gb'
    },
    {
      title:t("EPHEMERAL"),
      key:'ephemeral_gb'
    },
    {
      title:t("SWAP"),
      key:'swap'
    },
    {
      title:t("PUBLIC"),
      key:'is_public'
    },
    {
      title:'',
      key:'props'
    },
  ]

  const items = [
    {
      title:t('EDIT'),
      id:'3',
      icon:icons.Edit,
      onClick: () => {
        navigate(`edit`)
      },
      divider:false
    },
    {
      title:t("DELETE"),
      id:'4',
      onClick:() => {
        openModal('delete')
        setAlertTitle(t("FLAVOR_GROUP"))
        setAlertSubTitle([flavorGroup])
      },
      icon:icons.DeleteIcon,
      divider:false
    },
  ]

  const openModal = (modal:any) => {
    setModalType(modal)
    setViewModal(true)
    setActiveAnimation(true)
  }
  
  const closeModalCancel = () => {
    setViewModal(false)
    setActiveAnimation(false)
    setSelectedFlavorToRemove(null)
  }
  
  const _deleteFlavorGroup = async () => {
    if(flavorGroup){
      try {
        setLoading(true);
        await flavorService.deleteFlavorGroup(flavorGroup.id);
        setViewModal(false)
        setActiveAnimation(false)
        navigate('/openstack/flavor-groups')
      } catch (e: any) {
        console.log('e',e);
        if (e?.response?.data.detail) {
          viewAlert({
            severity: 'error',
            message: e.response.data.detail,
          });
        }
      } finally {
        setLoading(false);
      }
    }
  }

  const _addFlavorToGroup = async () => {
    if(selectedFlavor && groupId) {
      try {
        setLoading(true);
        await flavorService.addFlavorToGroup(selectedFlavor.value, {
          group_id: groupId
        });
        setViewModal(false)
        setActiveAnimation(false)
        setSelectedFlavor(null)
        _updateFlavorGroup()
        _updateFlavorsInGroup()
        viewAlert({
          severity: 'success',
          message: t("FLAVOR_ADDED_TO_GROUP"),
        });
      } catch (e: any) {
        console.log('e',e);
        if (e?.response?.data.detail) {
          viewAlert({
            severity: 'error',
            message: e.response.data.detail,
          });
        }
      } finally {
        setLoading(false);
      }
    }
  }

  const _removeFlavorFromGroup = async () => {
    if(selectedFlavorToRemove && groupId) {
      try {
        setLoading(true);
        await flavorService.removeFlavorFromGroup(selectedFlavorToRemove.flavor_id, {
          group_id: groupId
        });
        setViewModal(false)
        setActiveAnimation(false)
        setSelectedFlavorToRemove(null)
        _updateFlavorGroup()
        _updateFlavorsInGroup()
        viewAlert({
          severity: 'success',
          message: t("FLAVOR_REMOVED_FROM_GROUP"),
        });
      } catch (e: any) {
        console.log('e',e);
        if (e?.response?.data.detail) {
          viewAlert({
            severity: 'error',
            message: e.response.data.detail,
          });
        }
      } finally {
        setLoading(false);
      }
    }
  }

  const closeModal = (props:any) => {
    if(modalType === 'delete'){
      _deleteFlavorGroup()
    } else if(modalType === 'removeFromGroup'){
      _removeFlavorFromGroup()
    }
  }
  
  if(pageLoading || !flavorGroup) return (
    <Container>
      <TopProgress loading={pageLoading}/>
      <BodyWrapper>
        <InfoSkeleton size={9}/>
        <InfoSkeleton size={4}/>
      </BodyWrapper>
    </Container>
  )
  
  return(
    <Container>
      <BackArrow fallbackPath="/openstack/flavor-groups"/>
      <HeadWrapper>
        <div>
          <InstanceName>
            {flavorGroup?.name || flavorGroup?.id}
          </InstanceName>
        </div>
        <RightHeadContainer>
          <div ref={dropsRef}>
            <Button
              variant="stroke"
              size='small'
              icon={icons.More}
              isFill
              title={t("MENU")}
              onClick={() => setOptions(!options)}
            />
          </div>
          <PropsContainer style={{top:'35px', right:'0px'}} ref={optionsRef} active={options}>
            <DropOptions
              options={items}
            />
          </PropsContainer>
        </RightHeadContainer>
      </HeadWrapper>
      <TopInfoWrapper>
        <InfoBlock
          data={specifications}
          title={t("FLAVOR_GROUP_SPECIFICATIONS")}
          customStyles={{ width:"545px", flex:'auto 1', marginRight:'10px'}}
        />
      </TopInfoWrapper>
      
      <div style={{marginTop: '20px'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
          <InstanceName style={{fontSize: '20px'}}>
            {t("FLAVORS_IN_GROUP")}
          </InstanceName>
        </div>
        
        {paginationData && (
          <Table
            columns={flavorColumns}
            rows={flavors}
            settings={flavorItems}
            paginationData={paginationData}
            updateData={_updateFlavorsInGroup}
          />
        )}
        
        <div style={{marginTop: '20px', display: 'flex', alignItems: 'flex-end', gap: '10px'}}>
          <Select
            toolTip={false}
            data={allFlavors}
            fieldName='flavor'
            selectedValue={selectedFlavor?.value}
            onChange={(e:any) => {
              const found = allFlavors.find(f => f.value === e)
              setSelectedFlavor(found)
            }}
            title={t("SELECT_FLAVOR_TO_ADD")}
            customStyles={{width: '400px'}}
          />
          <Button
            variant="primary"
            size="display"
            onClick={_addFlavorToGroup}
            disabled={!selectedFlavor}
            loading={loading}
            title={t("ADD_TO_GROUP")}
          />
        </div>
      </div>
      
      <ModalVariables
        modalType={modalType}
        viewModal={viewModal}
        activeAnimation={activeAnimation}
        closeModal={closeModal}
        closeModalCancel={closeModalCancel}
        alertTitle={alertTitle}
        alertSubTitle={alertSubTitle}
        loading={loading}
        instance={selectedFlavorToRemove || flavorGroup}
      />
    </Container>
  )
}

export default FlavorGroupPage
